import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { ArrowRight, UploadCloud } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { getTMinus1, getTMinus2, toISO8601 } from '@fortem-monorepo/js-utilities'
import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { createContextMenu } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { FiltersProvider, useApi, useFilters } from '@fortem-monorepo/react-spa'
import { createFilters } from '@fortem-monorepo/react-spa'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { Premialab } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { PM } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import {
  useSubmitDailyCompositionMutation,
  useUploadToSFTPMutation,
} from '@/react-query/mutations/premialab'

const colDefs = createColDefs([
  {
    field: 'value_date',
    hide: true,
  },
  {
    field: 'value_date_mo',
    hide: true,
  },
  {
    field: 'PositionDate',
    width: 125,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'PortfolioName',
    width: 125,
    rowGroup: true,
    hide: true,
  },
  {
    field: 'PortfolioCurrency',
    width: 150,
  },
  {
    field: 'PortfolioNAV',
    width: 150,
    type: 'rightAligned',
    valueFormatter: numberFormatter({ delimiter: true }),
  },
  { field: 'PortfolioIndex', width: 150 },
  { field: 'ReferenceType', width: 125 },
  { field: 'Reference', width: 125 },
  {
    field: 'Units',
    width: 150,
    type: 'rightAligned',
    valueFormatter: numberFormatter({
      delimiter: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
  },
  {
    field: 'Level1',
    width: 150,
  },
  {
    field: 'Level2',
    width: 150,
  },
  {
    field: 'Level3',
    width: 150,
  },
  {
    field: 'RebalancingCost',
    width: 150,
  },
  {
    field: 'ReplicationCost',
    width: 150,
  },
  {
    field: 'FXHedged',
    width: 150,
  },
  {
    field: 'fxrate',
    width: 100,
    type: 'rightAligned',
    valueFormatter: numberFormatter({
      delimiter: true,
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }),
  },
  {
    field: 'Weights',
    width: 100,
    type: 'rightAligned',
    valueFormatter: numberFormatter({
      delimiter: true,
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }),
  },
  {
    field: 'UnitaryNAV',
    width: 150,
  },
  {
    field: 'NAVCCY',
    width: 100,
  },
  {
    field: 'instrumenttype',
    width: 150,
  },
  {
    field: 'internalreferencecode',
    width: 175,
  },
])

const filters = createFilters([
  {
    type: 'date',
    name: 'value_date',
    defaultValue: getTMinus1(),
    params: true,
  },
  {
    type: 'date',
    name: 'value_date_mo',
    defaultValue: getTMinus2(),
    params: true,
  },
])

export interface DailyCompositionPageProps {}

export const DailyCompositionPage: React.FC<DailyCompositionPageProps> = ({}) => {
  return (
    <Page
      metadata={{ title: 'Daily Composition' }}
      permissions={PM.PREMIALAB.DAILY_COMPOSITION.permissions}
    >
      <FiltersProvider filters={filters}>
        <DailyComposition />
      </FiltersProvider>
    </Page>
  )
}

export interface DailyCompositionProps {}

export const DailyComposition: React.FC<DailyCompositionProps> = ({}) => {
  const { hasPermissions } = useUserContext()
  const { values, registerFilter } = useFilters<typeof filters>()

  const hasWriteAccess = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_DAILY_COMPOSITION],
  }))

  const submitDailyCompositionMutation = useSubmitDailyCompositionMutation()
  const uploadToSFTPMutation = useUploadToSFTPMutation()

  const params: Premialab.GetDailyCompositionTemplateParams = {
    value_date: toISO8601(values.value_date!),
    value_date_mo: toISO8601(values.value_date_mo!),
  }

  const { Dialog, withConfirmation } = useConfirmModal({
    title: 'Upload to SFTP',
    description: (
      <>
        Confirm you want to upload all data to Premialab SFTP? Data will be overridden.{' '}
        <ul className={'list-disc pl-6'}>
          <li>Value Date: {params.value_date}</li>
          <li>Value Date-1: {params.value_date_mo}</li>
        </ul>
      </>
    ),
  })

  const api = useApi()

  const query = useQuery({
    queryKey: ['premialab', 'daily-composition', 'template', params],
    queryFn: () => api.premialab.getDailyComposition(params),
  })

  const data = useMemo(() => {
    return query.data ?? []
  }, [query.data])

  // @ts-expect-error
  const getContextMenuItems = createContextMenu((_params) => {
    return [
      ...(hasWriteAccess
        ? [
            {
              name: 'Upload to SFTP',
              icon: renderToString(<UploadCloud size={16} />),
              action: () => withConfirmation(() => uploadToSFTPMutation.mutateAsync(params)),
            },
            'separator',
          ]
        : []),
      'copy',
      'copyWithHeaders',
      'export',
    ]
  })

  return (
    <>
      <Dialog />
      <HeaderConfigBar>
        <div className="flex items-end gap-2">
          <FormItem>
            <Label className={'text-xs text-muted-foreground'}>Value Date</Label>
            <DatePicker size={'sm'} {...registerFilter('value_date')} />
          </FormItem>
          <FormItem>
            <Label className={'text-xs text-muted-foreground'}>Value Date Mo</Label>
            <DatePicker size={'sm'} {...registerFilter('value_date_mo')} />
          </FormItem>
          <CanAccess permissions={[PERMISSIONS.WRITE_DAILY_COMPOSITION]}>
            <LoadingButton
              size="sm"
              variant={'outline'}
              icon={<ArrowRight />}
              onClick={() => submitDailyCompositionMutation.mutateAsync(params)}
            >
              Submit
            </LoadingButton>
          </CanAccess>
        </div>
      </HeaderConfigBar>
      <Widget exposed className={'h-full'}>
        <WidgetHeaderWithQuery query={query} title={'Fund Composition Template'} />
        <WidgetContent loading={query.isLoading}>
          <CustomAgGrid
            rowData={data}
            getContextMenuItems={getContextMenuItems}
            columnDefs={colDefs}
            domLayout={'normal'}
          />
        </WidgetContent>
      </Widget>
    </>
  )
}
